import React from "react";
import PropTypes from "prop-types";
import { Box, TextField, Collapse } from "@material-ui/core";
import { BRAND } from "src/theme/brandTokens";
import { BOTON_BASE } from "./_botones";

// Tope de producto para el motivo de rechazo. Nace en el campo (con contador
// visible) para que quien escribe vea el límite antes de chocar con él, en
// vez de descubrirlo recién al enviar. Deliberadamente muy por debajo del
// techo de integridad que protege la columna en el backend: si los dos
// números coincidieran, el contador nunca se activaría y dejaría de cumplir
// su función. En la escala de los demás campos de motivo del módulo.
const MAX_LENGTH_MOTIVO = 1000;

// ─── Sub-componentes ────────────────────────────────────────────────────────

// Variantes especializadas del control de decisión: ACEPTAR (filled naranja)
// y RECHAZAR (filled inkSoft). minWidth fijo evita que el layout brinque al
// cambiar entre "ACEPTAR" y "✓ ACEPTADO" (longitudes distintas).
const BOTON_ESTILO_BASE = {
  ...BOTON_BASE,
  minWidth: "112pt",
};

const BotonAceptar = ({ activo, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    aria-pressed={activo}
    sx={{
      ...BOTON_ESTILO_BASE,
      bgcolor: activo ? BRAND.color.primary : "transparent",
      color: activo ? BRAND.color.paper : BRAND.color.primary,
      border: `1.5px solid ${BRAND.color.primary}`,
      "&:hover": activo
        ? {
            bgcolor: BRAND.color.primaryDark,
            borderColor: BRAND.color.primaryDark,
          }
        : { bgcolor: "rgba(213, 122, 35, 0.08)" },
      "&:focus-visible": {
        outline: `2px solid ${BRAND.color.primary}`,
        outlineOffset: 2,
      },
    }}
  >
    {activo ? "✓ ACEPTADO" : "ACEPTAR"}
  </Box>
);

const BotonRechazar = ({ activo, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    aria-pressed={activo}
    sx={{
      ...BOTON_ESTILO_BASE,
      bgcolor: activo ? BRAND.color.inkSoft : "transparent",
      color: activo ? BRAND.color.paper : BRAND.color.grey70,
      border: `1.5px solid ${activo ? BRAND.color.inkSoft : BRAND.color.grey70}`,
      "&:hover": activo
        ? { bgcolor: BRAND.color.ink, borderColor: BRAND.color.ink }
        : { bgcolor: "rgba(0, 0, 0, 0.04)" },
      "&:focus-visible": {
        outline: `2px solid ${BRAND.color.grey70}`,
        outlineOffset: 2,
      },
    }}
  >
    {activo ? "✗ RECHAZADO" : "RECHAZAR"}
  </Box>
);

// El recuadro de solo lectura cubre las tres decisiones que puede tener un
// item ya respondido. Antes solo distinguía dos (una bandera booleana
// "aceptado"), así que la tercera decisión posible -el servicio que el
// cliente pidió ajustar- caía en la rama del "no aceptado" y se mostraba
// como un rechazo que nunca ocurrió.
const CONFIG_TERMINAL = {
  ACEPTADO: {
    label: "✓ ACEPTADO",
    bgcolor: BRAND.color.primary,
    color: BRAND.color.paper,
    border: BRAND.color.primary,
  },
  OBSERVADO: {
    label: "○ AJUSTE EN REVISIÓN",
    bgcolor: BRAND.color.primaryBg,
    color: BRAND.color.primaryDark,
    border: BRAND.color.primary,
  },
  RECHAZADO: {
    label: "✗ RECHAZADO",
    bgcolor: BRAND.color.inkSoft,
    color: BRAND.color.paper,
    border: BRAND.color.inkSoft,
  },
};

// Respaldo para una decision que este mapa no conozca: muestra su nombre crudo
// en tratamiento neutro. Hace falta porque el conjunto de decisiones ya crecio
// una vez -de dos a tres- y esta pantalla la ve el cliente: sin respaldo, un
// valor nuevo deja la propuesta en blanco, y reusar el estilo del rechazo le
// atribuiria al cliente una decision que no tomo.
const configTerminal = (decision) =>
  CONFIG_TERMINAL[decision] ?? {
    label: decision,
    bgcolor: BRAND.color.grey10,
    color: BRAND.color.ink,
    border: BRAND.color.divider,
  };

// Aspecto del ítem que quedó sin decidir cuando la pantalla ya no admite
// acciones. Se define aparte y no como una entrada más de CONFIG_TERMINAL
// porque ese mapa traduce decisiones que tomó el cliente y esto es la ausencia
// de una: sumarla ahí obligaría a quien lo lea después a distinguir cuáles de
// sus claves llegan desde el backend y cuál se la inventa esta pantalla, y
// bastaría con recorrer sus llaves para creer que existe una cuarta decisión.
// Tampoco puede salir del respaldo neutro de arriba, que atiende otro caso -una
// decisión futura que el mapa todavía no conozca- y muestra su nombre crudo.
const CONFIG_SIN_RESPUESTA = {
  label: "SIN RESPUESTA",
  bgcolor: BRAND.color.grey10,
  color: BRAND.color.grey70,
  border: BRAND.color.divider,
};

// Marco común de las insignias del recuadro de solo lectura: mismo alto, mismo
// ancho mínimo y misma tipografía que los botones a los que reemplazan, para
// que la lista de servicios se lea pareja de un vistazo. Compartirlo en vez de
// copiar el bloque de estilos evita que una insignia se quede atrás el día que
// el tratamiento visual cambie.
const InsigniaBase = ({ conf, children }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: "8pt",
      alignItems: "flex-end",
    }}
  >
    <Box
      sx={{
        ...BOTON_ESTILO_BASE,
        cursor: "default",
        bgcolor: conf.bgcolor,
        color: conf.color,
        border: `1.5px solid ${conf.border}`,
      }}
    >
      {conf.label}
    </Box>
    {children}
  </Box>
);

const BadgeTerminal = ({
  decision,
  comentarioCliente,
  notaRondaAnterior = false,
}) => (
  <InsigniaBase conf={configTerminal(decision)}>
    {decision !== "ACEPTADO" && comentarioCliente && (
      <Box
        sx={{
          maxWidth: 400,
          p: 1.5,
          border: `1px solid ${BRAND.color.divider}`,
          bgcolor: BRAND.color.grey10,
          fontFamily: BRAND.font.family.base,
        }}
      >
        <Box
          sx={{
            fontSize: BRAND.font.size.eyebrow,
            color: BRAND.color.grey70,
            textTransform: "uppercase",
            letterSpacing: BRAND.font.letterSpacing.wide,
            fontWeight: BRAND.font.weight.medium,
            mb: 0.5,
          }}
        >
          Motivo indicado
        </Box>
        <Box
          sx={{
            fontSize: BRAND.font.size.bodySmall,
            color: BRAND.color.inkSoft,
            lineHeight: BRAND.font.lineHeight.body,
            whiteSpace: "pre-wrap",
          }}
        >
          {comentarioCliente}
        </Box>
      </Box>
    )}
    {notaRondaAnterior && (
      <Box
        sx={{
          maxWidth: 400,
          fontSize: BRAND.font.size.bodySmall,
          color: BRAND.color.grey70,
          lineHeight: BRAND.font.lineHeight.body,
          textAlign: "right",
        }}
      >
        Aceptado en una ronda anterior. No necesitas volver a decidirlo.
      </Box>
    )}
  </InsigniaBase>
);

// El servicio que nadie decidió, en una pantalla cerrada, también muestra
// insignia y no botones apagados: donde el cliente ya no puede actuar, un
// control deshabilitado invita a preguntarse qué falta para encenderlo y la
// respuesta es que ya se acabó. No lleva bloque de motivo ni nota de ronda
// anterior porque sin respuesta del cliente no hay nada que citar.
const BadgeSinRespuesta = () => <InsigniaBase conf={CONFIG_SIN_RESPUESTA} />;

// ─── Componente principal ───────────────────────────────────────────────────

const ItemDecisionControl = ({
  decision,
  motivo,
  onChange,
  readOnly,
  respuestaPrevia,
}) => {
  // Pantalla no accionable (estado terminal, conversación en curso o enlace
  // vencido): el cliente ya no puede decidir, así que ningún ítem ofrece
  // botones. El que tiene respuesta previa muestra su resultado final, sea
  // cual sea la decisión; el que no la tiene dice que quedó sin responder.
  // Sin esa segunda rama el caso más común -la propuesta que caduca porque el
  // cliente nunca contestó, y por lo tanto no tiene ninguna respuesta- cae al
  // render normal y pinta botones de aceptar y rechazar que no envían nada,
  // porque la barra de firma ni siquiera se monta en esta pantalla.
  // La condición mira solo `readOnly` para que la promesa sea estructural:
  // cualquier ítem que llegue acá sale con insignia, tenga o no respuesta.
  if (readOnly) {
    return respuestaPrevia ? (
      <BadgeTerminal
        decision={respuestaPrevia.decision}
        comentarioCliente={respuestaPrevia.comentarioCliente}
      />
    ) : (
      <BadgeSinRespuesta />
    );
  }

  // Pantalla accionable (ronda nueva) con este ítem ya aceptado en una ronda
  // anterior: el servicio se materializó y no vuelve a discutirse, aunque el
  // resto de la propuesta siga abierta a decisión. RECHAZADO y OBSERVADO no
  // cierran el ítem -vuelven a la mesa en cada ronda nueva-, así que esos dos
  // casos siguen de largo hacia los botones, igual que un ítem sin respuesta.
  if (!readOnly && respuestaPrevia?.decision === "ACEPTADO") {
    return (
      <BadgeTerminal
        decision={respuestaPrevia.decision}
        comentarioCliente={respuestaPrevia.comentarioCliente}
        notaRondaAnterior
      />
    );
  }

  // El feedback al cliente vive en tres capas visuales sincronizadas: el
  // botón mismo (outline -> filled con check/cruz), el wrapper card (borde +
  // fondo + opacidad según el estado) y el contador del StickyActionBar.
  // No se dispara toast porque "Servicio aceptado" sin envío real puede
  // hacer creer al cliente que la respuesta ya quedó registrada cuando solo
  // se marcó el item — el envío ocurre al firmar con OTP.
  const aceptar = () => {
    if (decision === "ACEPTADO") return;
    onChange({ decision: "ACEPTADO", motivo: "" });
  };

  const rechazar = () => {
    if (decision === "RECHAZADO") return;
    onChange({ decision: "RECHAZADO", motivo: motivo || "" });
  };

  const handleChangeMotivo = (e) => {
    onChange({ decision, motivo: e.target.value });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          justifyContent: "flex-end",
        }}
      >
        <BotonAceptar activo={decision === "ACEPTADO"} onClick={aceptar} />
        <BotonRechazar activo={decision === "RECHAZADO"} onClick={rechazar} />
      </Box>

      <Collapse in={decision === "RECHAZADO"}>
        <Box sx={{ pl: { xs: 0, md: 1 } }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            placeholder="Motivo opcional (ayuda al vendedor a proponer una alternativa)"
            value={motivo || ""}
            onChange={handleChangeMotivo}
            variant="outlined"
            size="small"
            inputProps={{ maxLength: MAX_LENGTH_MOTIVO }}
            helperText={`${(motivo || "").length}/${MAX_LENGTH_MOTIVO} caracteres`}
            FormHelperTextProps={{ sx: { textAlign: "right", mx: 0 } }}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── PropTypes ──────────────────────────────────────────────────────────────

BotonAceptar.propTypes = {
  activo: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

BotonRechazar.propTypes = {
  activo: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

InsigniaBase.propTypes = {
  conf: PropTypes.shape({
    label: PropTypes.string.isRequired,
    bgcolor: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    border: PropTypes.string.isRequired,
  }).isRequired,
  children: PropTypes.node,
};

InsigniaBase.defaultProps = {
  children: null,
};

BadgeTerminal.propTypes = {
  decision: PropTypes.oneOf(["ACEPTADO", "RECHAZADO", "OBSERVADO"]).isRequired,
  comentarioCliente: PropTypes.string,
  notaRondaAnterior: PropTypes.bool,
};

ItemDecisionControl.propTypes = {
  decision: PropTypes.oneOf(["ACEPTADO", "RECHAZADO", null]),
  motivo: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  respuestaPrevia: PropTypes.shape({
    decision: PropTypes.oneOf(["ACEPTADO", "RECHAZADO", "OBSERVADO"])
      .isRequired,
    comentarioCliente: PropTypes.string,
    fechaDecision: PropTypes.string,
  }),
};

ItemDecisionControl.defaultProps = {
  decision: null,
  motivo: "",
  readOnly: false,
  respuestaPrevia: null,
};

export default ItemDecisionControl;
